'use client';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { DesktopStillHandle, DesktopStillOptions } from 'elowen-plugin-ui-kit';

const response = z.object({
  dataUrl: z.union([
    z.string().startsWith('data:image/png;base64,'),
    z.string().startsWith('data:image/jpeg;base64,'),
    z.string().startsWith('data:image/webp;base64,'),
  ]).pipe(z.string().max(1024 * 1024 + 32)).nullable(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  refreshMs: z.number().int().positive().max(60000),
  liveForMs: z.number().int().positive().max(60000),
});

/** One scheduling/freshness boundary for every producer. A mounted tile is not necessarily visible. */
export function useDesktopStill({ queryKey, enabled, queryFn }: DesktopStillOptions): DesktopStillHandle {
  const [node, ref] = useState<HTMLElement | null>(null);
  const [intersection, setIntersection] = useState<HTMLElement | null>(null);
  const [visible, setVisible] = useState(() => document.visibilityState !== 'hidden');
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const changed = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', changed);
    return () => document.removeEventListener('visibilitychange', changed);
  }, []);
  useEffect(() => {
    if (!node) return;
    const observer = new IntersectionObserver(entries => {
      const entry = entries.find(value => value.target === node);
      if (entry) setIntersection(entry.isIntersecting ? node : null);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  const query = useQuery({
    queryKey,
    enabled: enabled && visible && !!node && intersection === node,
    queryFn: async ({ signal }) => ({
      ...response.parse(await queryFn({ signal })),
      receivedAt: Date.now(),
    }),
    refetchInterval: value => value.state.data?.refreshMs ?? 1500,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const value = query.data;
  const expiresAt = value ? value.receivedAt + value.liveForMs : 0;
  useEffect(() => {
    if (!expiresAt) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.max(0, expiresAt - Date.now()) + 1);
    return () => clearTimeout(timer);
  }, [expiresAt]);
  if (query.isError || value && Math.max(now, Date.now()) >= expiresAt) {
    return { ref, dataUrl: null, aspect: null, state: 'stalled' };
  }
  return {
    ref,
    dataUrl: value?.dataUrl ?? null,
    aspect: value?.width && value.height ? value.width / value.height : null,
    state: value ? value.dataUrl ? 'ready' : 'stalled' : 'loading',
  };
}
